import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { AxiosResponse } from 'axios'
import { toast } from 'react-toastify'
import {
  IMachineCategoryNetworkClass,
  ISetMachineCategoryNetworkClassRequest,
  ISiteNetworkActionResult,
  ISiteNetworkActivateDraftResponse,
  ISiteNetworkCreateAnchorRequest,
  ISiteNetworkCreateGeofenceRequest,
  ISiteNetworkCreateNodeRequest,
  ISiteNetworkCreateRoadEventRequest,
  ISiteNetworkCreateSegmentRequest,
  ISiteNetworkDraftFeature,
  ISiteNetworkDrawSegmentRequest,
  ISiteNetworkEtaResponse,
  ISiteNetworkGeofence,
  ISiteNetworkGraph,
  ISiteNetworkGpsPingInput,
  ISiteNetworkIngestResult,
  ISiteNetworkLocationAnchor,
  ISiteNetworkMatrixRequest,
  ISiteNetworkMatrixResponse,
  ISiteNetworkNodeResponse,
  ISiteNetworkProposal,
  ISiteNetworkRoadEvent,
  ISiteNetworkRouteRequest,
  ISiteNetworkRouteResponse,
  ISiteNetworkSegmentResponse,
  ISiteNetworkSetEtaRequest,
  ISiteNetworkSnapRequest,
  ISiteNetworkSnapResponse,
} from 'types/api/siteNetworkApi.interface'
import { ISiteNetworkState } from 'types/siteNetwork.interface'
import { NetworkNodeKind } from 'types/siteNetworkEnums.interface'
import { baseUrl } from 'utils/axios'
import { handleErrors } from 'utils/helpers'

const prefix = (siteNodeId: number) => `site-network/${siteNodeId}`

const initialState: ISiteNetworkState = {
  isLoading: false,
  siteNodeId: null,
  graph: null,
  draftFeatures: [],
  geofences: [],
  roadEvents: [],
  proposals: [],
  machineClasses: [],
  route: null,
  isRouting: false,
}

const haversineM = (a: number[], b: number[]): number => {
  const toRad = (d: number) => (d * Math.PI) / 180
  const R = 6371000
  const dLat = toRad(b[1] - a[1])
  const dLon = toRad(b[0] - a[0])
  const lat1 = toRad(a[1])
  const lat2 = toRad(b[1])
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2
  return 2 * R * Math.asin(Math.sqrt(h))
}

const nearestNode = (
  graph: ISiteNetworkGraph | null,
  draftFeatures: ISiteNetworkDraftFeature[],
  coord: number[],
  maxM = 30,
): { id: string; lon: number; lat: number } | null => {
  let best: { id: string; lon: number; lat: number } | null = null
  let bestD = maxM
  const consider = (id: string, lon: number, lat: number) => {
    const d = haversineM(coord, [lon, lat])
    if (d <= bestD) {
      bestD = d
      best = { id, lon, lat }
    }
  }
  ;(graph?.features || []).forEach((f) => {
    if (f.geometry?.type !== 'Point') return
    const [lon, lat] = f.geometry.coordinates as number[]
    const id = String(f.properties?.id ?? '')
    if (id) consider(id, lon, lat)
  })
  draftFeatures.forEach((d) => {
    const from = d.coordinates[0]
    const to = d.coordinates[d.coordinates.length - 1]
    if (from) consider(d.fromNodeId, from[0], from[1])
    if (to) consider(d.toNodeId, to[0], to[1])
  })
  return best
}

export const getNetworkGraph = createAsyncThunk(
  'siteNetwork/getNetworkGraph',
  async (siteNodeId: number, thunkAPI) => {
    try {
      const resp: AxiosResponse<ISiteNetworkGraph> = await baseUrl.get(`${prefix(siteNodeId)}/graph`)
      return { siteNodeId, graph: resp.data }
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getRoadEvents = createAsyncThunk(
  'siteNetwork/getRoadEvents',
  async (siteNodeId: number, thunkAPI) => {
    try {
      const resp: AxiosResponse<ISiteNetworkRoadEvent[]> = await baseUrl.get(
        `${prefix(siteNodeId)}/road-events`,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getGeofences = createAsyncThunk(
  'siteNetwork/getGeofences',
  async (p: { siteNodeId: number; kind?: number; includeInactive?: boolean }, thunkAPI) => {
    try {
      const params: any = {}
      if (p.kind != null) params.kind = p.kind
      if (p.includeInactive != null) params.includeInactive = p.includeInactive
      const resp: AxiosResponse<ISiteNetworkGeofence[]> = await baseUrl.get(
        `${prefix(p.siteNodeId)}/geofences`,
        { params },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getProposals = createAsyncThunk(
  'siteNetwork/getProposals',
  async (siteNodeId: number, thunkAPI) => {
    try {
      const resp: AxiosResponse<ISiteNetworkProposal[]> = await baseUrl.get(
        `${prefix(siteNodeId)}/proposals`,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getMachineClasses = createAsyncThunk(
  'siteNetwork/getMachineClasses',
  async (_, thunkAPI) => {
    try {
      const resp: AxiosResponse<IMachineCategoryNetworkClass[]> = await baseUrl.get(
        'site-network/machine/classes',
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const setMachineClass = createAsyncThunk(
  'siteNetwork/setMachineClass',
  async (
    p: { machineCategoryId: number; body: ISetMachineCategoryNetworkClassRequest },
    thunkAPI,
  ) => {
    try {
      const resp: AxiosResponse<IMachineCategoryNetworkClass> = await baseUrl.put(
        `site-network/machine/classes/${p.machineCategoryId}`,
        p.body,
      )
      await thunkAPI.dispatch(getMachineClasses())
      toast.success('Machine class mapping saved')
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const calculateRoute = createAsyncThunk(
  'siteNetwork/calculateRoute',
  async (p: { siteNodeId: number; body: ISiteNetworkRouteRequest }, thunkAPI) => {
    try {
      const resp: AxiosResponse<ISiteNetworkRouteResponse> = await baseUrl.post(
        `${prefix(p.siteNodeId)}/route`,
        p.body,
      )
      return resp.data
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const calculateMatrix = createAsyncThunk(
  'siteNetwork/calculateMatrix',
  async (p: { siteNodeId: number; body: ISiteNetworkMatrixRequest }, thunkAPI) => {
    try {
      const resp: AxiosResponse<ISiteNetworkMatrixResponse> = await baseUrl.post(
        `${prefix(p.siteNodeId)}/matrix`,
        p.body,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const snapCoordinate = createAsyncThunk(
  'siteNetwork/snapCoordinate',
  async (p: { siteNodeId: number; body: ISiteNetworkSnapRequest }, thunkAPI) => {
    try {
      const resp: AxiosResponse<ISiteNetworkSnapResponse | null> = await baseUrl.post(
        `${prefix(p.siteNodeId)}/snap`,
        p.body,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getMachineEta = createAsyncThunk(
  'siteNetwork/getMachineEta',
  async (p: { siteNodeId: number; machineId: number }, thunkAPI) => {
    try {
      const resp: AxiosResponse<ISiteNetworkEtaResponse> = await baseUrl.get(
        `${prefix(p.siteNodeId)}/eta/${p.machineId}`,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const setMachineEta = createAsyncThunk(
  'siteNetwork/setMachineEta',
  async (
    p: { siteNodeId: number; machineId: number; body: ISiteNetworkSetEtaRequest },
    thunkAPI,
  ) => {
    try {
      const resp: AxiosResponse<ISiteNetworkEtaResponse> = await baseUrl.put(
        `${prefix(p.siteNodeId)}/eta/${p.machineId}/destination`,
        p.body,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const createNetworkNode = createAsyncThunk(
  'siteNetwork/createNetworkNode',
  async (p: { siteNodeId: number; body: ISiteNetworkCreateNodeRequest }, thunkAPI) => {
    try {
      const resp: AxiosResponse<ISiteNetworkNodeResponse> = await baseUrl.post(
        `${prefix(p.siteNodeId)}/nodes`,
        p.body,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const createNetworkSegment = createAsyncThunk(
  'siteNetwork/createNetworkSegment',
  async (p: { siteNodeId: number; body: ISiteNetworkCreateSegmentRequest }, thunkAPI) => {
    try {
      const resp: AxiosResponse<ISiteNetworkSegmentResponse> = await baseUrl.post(
        `${prefix(p.siteNodeId)}/segments`,
        p.body,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const drawRoadSegment = createAsyncThunk(
  'siteNetwork/drawRoadSegment',
  async (p: ISiteNetworkDrawSegmentRequest, thunkAPI) => {
    try {
      const state = thunkAPI.getState() as { siteNetwork: ISiteNetworkState }
      const coords = p.coordinates.map((c) => [...c])
      if (!coords || coords.length < 2) {
        throw new Error('A road needs at least 2 points')
      }

      const fromSnap = p.existingFromNodeId
        ? { id: p.existingFromNodeId, lon: coords[0][0], lat: coords[0][1] }
        : nearestNode(state.siteNetwork.graph, state.siteNetwork.draftFeatures, coords[0])
      const toSnap = p.existingToNodeId
        ? {
            id: p.existingToNodeId,
            lon: coords[coords.length - 1][0],
            lat: coords[coords.length - 1][1],
          }
        : nearestNode(
            state.siteNetwork.graph,
            state.siteNetwork.draftFeatures,
            coords[coords.length - 1],
          )

      let fromNodeId = fromSnap?.id ?? null
      let toNodeId = toSnap?.id ?? null

      if (fromSnap) {
        coords[0] = [fromSnap.lon, fromSnap.lat, coords[0][2] ?? 0]
      }

      if (toSnap) {
        const lastIdx = coords.length - 1
        coords[lastIdx] = [toSnap.lon, toSnap.lat, coords[lastIdx][2] ?? 0]
      }

      if (!fromNodeId) {
        const fromNode = await thunkAPI
          .dispatch(
            createNetworkNode({
              siteNodeId: p.siteNodeId,
              body: {
                kind: NetworkNodeKind.Junction,
                lon: coords[0][0],
                lat: coords[0][1],
                elevation: coords[0][2] ?? 0,
              },
            }),
          )
          .unwrap()
        fromNodeId = fromNode.id
      }

      if (!toNodeId) {
        const last = coords[coords.length - 1]
        const toNode = await thunkAPI
          .dispatch(
            createNetworkNode({
              siteNodeId: p.siteNodeId,
              body: {
                kind: NetworkNodeKind.Junction,
                lon: last[0],
                lat: last[1],
                elevation: last[2] ?? 0,
              },
            }),
          )
          .unwrap()
        toNodeId = toNode.id
      }

      const segment = await thunkAPI
        .dispatch(
          createNetworkSegment({
            siteNodeId: p.siteNodeId,
            body: {
              fromNodeId,
              toNodeId,
              coordinates: coords,
              class: p.class,
              name: p.name,
              oneWay: p.oneWay,
            },
          }),
        )
        .unwrap()

      toast.success('Road saved as DRAFT')
      return {
        id: segment.id,
        name: segment.name,
        class: Number(segment.class),
        coordinates: coords,
        fromNodeId,
        toNodeId,
      } as ISiteNetworkDraftFeature
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const activateSegment = createAsyncThunk(
  'siteNetwork/activateSegment',
  async (p: { siteNodeId: number; id: string }, thunkAPI) => {
    try {
      const resp: AxiosResponse<ISiteNetworkActionResult> = await baseUrl.post(
        `${prefix(p.siteNodeId)}/segments/${p.id}/activate`,
      )
      await thunkAPI.dispatch(getNetworkGraph(p.siteNodeId))
      toast.success('Segment activated')
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const retireSegment = createAsyncThunk(
  'siteNetwork/retireSegment',
  async (p: { siteNodeId: number; id: string }, thunkAPI) => {
    try {
      const resp: AxiosResponse<ISiteNetworkActionResult> = await baseUrl.post(
        `${prefix(p.siteNodeId)}/segments/${p.id}/retire`,
      )
      await thunkAPI.dispatch(getNetworkGraph(p.siteNodeId))
      toast.success('Segment retired')
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const activateDrafts = createAsyncThunk(
  'siteNetwork/activateDrafts',
  async (siteNodeId: number, thunkAPI) => {
    try {
      const resp: AxiosResponse<ISiteNetworkActivateDraftResponse> = await baseUrl.post(
        `${prefix(siteNodeId)}/activate-draft`,
      )
      await thunkAPI.dispatch(getNetworkGraph(siteNodeId))
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const createRoadEvent = createAsyncThunk(
  'siteNetwork/createRoadEvent',
  async (p: { siteNodeId: number; body: ISiteNetworkCreateRoadEventRequest }, thunkAPI) => {
    try {
      const resp: AxiosResponse<ISiteNetworkRoadEvent> = await baseUrl.post(
        `${prefix(p.siteNodeId)}/road-events`,
        p.body,
      )
      await thunkAPI.dispatch(getRoadEvents(p.siteNodeId))
      await thunkAPI.dispatch(getNetworkGraph(p.siteNodeId))
      toast.success('Road event created — routing updated')
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const cancelRoadEvent = createAsyncThunk(
  'siteNetwork/cancelRoadEvent',
  async (p: { siteNodeId: number; id: string }, thunkAPI) => {
    try {
      const resp: AxiosResponse<ISiteNetworkActionResult> = await baseUrl.delete(
        `${prefix(p.siteNodeId)}/road-events/${p.id}`,
      )
      await thunkAPI.dispatch(getRoadEvents(p.siteNodeId))
      await thunkAPI.dispatch(getNetworkGraph(p.siteNodeId))
      toast.success('Road event canceled')
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const createAnchor = createAsyncThunk(
  'siteNetwork/createAnchor',
  async (p: { siteNodeId: number; body: ISiteNetworkCreateAnchorRequest }, thunkAPI) => {
    try {
      const resp: AxiosResponse<ISiteNetworkLocationAnchor> = await baseUrl.post(
        `${prefix(p.siteNodeId)}/anchors`,
        p.body,
      )
      toast.success('Location anchored to network node')
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const createGeofence = createAsyncThunk(
  'siteNetwork/createGeofence',
  async (p: { siteNodeId: number; body: ISiteNetworkCreateGeofenceRequest }, thunkAPI) => {
    try {
      const resp: AxiosResponse<ISiteNetworkGeofence> = await baseUrl.post(
        `${prefix(p.siteNodeId)}/geofences`,
        p.body,
      )
      toast.success('Geofence created')
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const acceptProposal = createAsyncThunk(
  'siteNetwork/acceptProposal',
  async (p: { siteNodeId: number; id: string }, thunkAPI) => {
    try {
      const resp: AxiosResponse<ISiteNetworkActionResult> = await baseUrl.post(
        `${prefix(p.siteNodeId)}/proposals/${p.id}/accept`,
      )
      await thunkAPI.dispatch(getProposals(p.siteNodeId))
      toast.success('Proposal accepted')
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const dismissProposal = createAsyncThunk(
  'siteNetwork/dismissProposal',
  async (p: { siteNodeId: number; id: string }, thunkAPI) => {
    try {
      const resp: AxiosResponse<ISiteNetworkActionResult> = await baseUrl.post(
        `${prefix(p.siteNodeId)}/proposals/${p.id}/dismiss`,
      )
      await thunkAPI.dispatch(getProposals(p.siteNodeId))
      toast.success('Proposal dismissed')
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const ingestGpsPings = createAsyncThunk(
  'siteNetwork/ingestGpsPings',
  async (p: { siteNodeId: number; pings: ISiteNetworkGpsPingInput[] }, thunkAPI) => {
    try {
      const resp: AxiosResponse<ISiteNetworkIngestResult> = await baseUrl.post(
        `${prefix(p.siteNodeId)}/ingest/pings`,
        { pings: p.pings },
      )
      toast.success(`Ingested ${resp.data.ingested} pings`)
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

const siteNetworkSlice = createSlice({
  name: 'siteNetwork',
  initialState,
  reducers: {
    clearSiteNetwork: () => initialState,
    clearSiteNetworkRoute: (state) => {
      state.route = null
    },
    setSiteNodeId: (state, { payload }: { payload: number | null }) => {
      state.siteNodeId = payload
      state.graph = null
      state.draftFeatures = []
      state.geofences = []
      state.roadEvents = []
      state.proposals = []
      state.route = null
    },
    clearDraftFeatures: (state) => {
      state.draftFeatures = []
    },
    hydrateDraftFeatures: (state, { payload }: { payload: ISiteNetworkDraftFeature[] }) => {
      state.draftFeatures = payload || []
    },
  },
  extraReducers: {
    [getNetworkGraph.pending.type]: (state) => {
      state.isLoading = true
    },
    [getNetworkGraph.fulfilled.type]: (
      state,
      { payload }: { payload: { siteNodeId: number; graph: ISiteNetworkGraph } },
    ) => {
      state.isLoading = false
      state.siteNodeId = payload.siteNodeId
      state.graph = payload.graph
    },
    [getNetworkGraph.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getRoadEvents.fulfilled.type]: (state, { payload }: { payload: ISiteNetworkRoadEvent[] }) => {
      state.roadEvents = payload.filter((e) => !e.canceledAt)
    },
    [getRoadEvents.rejected.type]: (_state, { payload }) => {
      handleErrors(payload)
    },
    [getProposals.fulfilled.type]: (state, { payload }: { payload: ISiteNetworkProposal[] }) => {
      state.proposals = payload
    },
    [getProposals.rejected.type]: (_state, { payload }) => {
      handleErrors(payload)
    },
    [getMachineClasses.fulfilled.type]: (
      state,
      { payload }: { payload: IMachineCategoryNetworkClass[] },
    ) => {
      state.machineClasses = payload
    },
    [getMachineClasses.rejected.type]: (_state, { payload }) => {
      handleErrors(payload)
    },
    [setMachineClass.rejected.type]: (_state, { payload }) => {
      handleErrors(payload)
    },
    [calculateRoute.pending.type]: (state) => {
      state.isRouting = true
    },
    [calculateRoute.fulfilled.type]: (
      state,
      { payload }: { payload: ISiteNetworkRouteResponse },
    ) => {
      state.isRouting = false
      state.route = payload
    },
    [calculateRoute.rejected.type]: (state, { payload }: { payload: any }) => {
      state.isRouting = false
      const code = payload?.response?.data?.code
      if (code === 'NO_ROUTE') {
        toast.error('No permitted path (check closures / vehicle class)')
        return
      }
      if (code === 'SNAP_FAILED') {
        toast.error('Could not snap endpoint to a road (nothing within range)')
        return
      }
      handleErrors(payload)
    },
    [drawRoadSegment.fulfilled.type]: (
      state,
      { payload }: { payload: ISiteNetworkDraftFeature },
    ) => {
      state.draftFeatures = [...state.draftFeatures, payload]
    },
    [drawRoadSegment.rejected.type]: (_state, { payload }) => {
      handleErrors(payload)
    },
    [activateDrafts.fulfilled.type]: (
      state,
      { payload }: { payload: ISiteNetworkActivateDraftResponse },
    ) => {
      state.draftFeatures = []
      toast.success(
        `Activated ${payload.segmentsActivated} segments / ${payload.nodesActivated} nodes`,
      )
    },
    [activateDrafts.rejected.type]: (_state, { payload }) => {
      handleErrors(payload)
    },
    [createRoadEvent.rejected.type]: (_state, { payload }) => {
      handleErrors(payload)
    },
    [cancelRoadEvent.rejected.type]: (_state, { payload }) => {
      handleErrors(payload)
    },
    [createNetworkNode.rejected.type]: (_state, { payload }) => {
      handleErrors(payload)
    },
    [createNetworkSegment.rejected.type]: (_state, { payload }) => {
      handleErrors(payload)
    },
    [acceptProposal.rejected.type]: (_state, { payload }) => {
      handleErrors(payload)
    },
    [dismissProposal.rejected.type]: (_state, { payload }) => {
      handleErrors(payload)
    },
    [createAnchor.rejected.type]: (_state, { payload }) => {
      handleErrors(payload)
    },
    [createGeofence.rejected.type]: (_state, { payload }) => {
      handleErrors(payload)
    },
    [createGeofence.fulfilled.type]: (state, { payload }: { payload: ISiteNetworkGeofence }) => {
      state.geofences = [payload, ...state.geofences.filter((g) => g.id !== payload.id)]
    },
    [getGeofences.fulfilled.type]: (state, { payload }: { payload: ISiteNetworkGeofence[] }) => {
      state.geofences = payload
    },
    [getGeofences.rejected.type]: (_state, { payload }) => {
      handleErrors(payload)
    },
    [ingestGpsPings.rejected.type]: (_state, { payload }) => {
      handleErrors(payload)
    },
    [getMachineEta.rejected.type]: (_state, { payload }) => {
      handleErrors(payload)
    },
    [setMachineEta.rejected.type]: (_state, { payload }) => {
      handleErrors(payload)
    },
    [activateSegment.rejected.type]: (_state, { payload }) => {
      handleErrors(payload)
    },
    [retireSegment.rejected.type]: (_state, { payload }) => {
      handleErrors(payload)
    },
  },
})

export const {
  clearSiteNetwork,
  clearSiteNetworkRoute,
  setSiteNodeId,
  clearDraftFeatures,
  hydrateDraftFeatures,
} = siteNetworkSlice.actions

export default siteNetworkSlice.reducer
